// src/config/roleplay.js
// Client-side defaults for the SDR Roleplay tool: the scenario tracks, the
// three difficulty tiers, the scoring rubric, and a couple of starter personas.
//
// The live source of truth is Firestore (`roleplay_config` + `roleplay_personas`),
// editable in-app by admins. These constants are used to (a) seed a fresh
// project and (b) render the setup screen before/without custom config. Keep the
// difficulty/rubric shapes in sync with functions/defaults.js.

// The Claude model powering roleplay replies + scoring. Admin-selectable in the
// Personas tab; the choice is stored in roleplay_config/model and read by the
// Cloud Functions at call time (DEFAULT_MODEL is the fallback there too).
export const DEFAULT_MODEL = 'claude-sonnet-5';

export const MODELS = [
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', note: 'Recommended — fast, strong, good value.' },
  { id: 'claude-opus-5', label: 'Claude Opus 5', note: 'Highest quality; slower and more expensive.' },
  { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', note: 'Fastest and cheapest; lighter quality.' },
];

// Cost guardrails, mirrored from functions/index.js DEFAULT_LIMITS. Admin-tunable
// in the Personas tab; stored in roleplay_config/limits and enforced server-side.
export const DEFAULT_LIMITS = {
  enabled: true,
  maxTurnsPerCall: 30,
  dailyTurnsPerRep: 200,
  maxMessageChars: 2000,
};

// Format a USD cost estimate for display. Small values keep more decimals.
export function formatUsd(n) {
  const v = Number(n) || 0;
  if (v === 0) return '$0.00';
  if (v < 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
}

export const TRACKS = [
  {
    key: 'cold-open',
    label: 'Cold open',
    description: 'The first 60 seconds — earn the right to keep talking.',
  },
  {
    key: 'discovery',
    label: 'Discovery',
    description: 'You got past the opener; now uncover the real problem.',
  },
];

export const DIFFICULTIES = [
  { key: 'cooperative', label: 'Cooperative', blurb: 'Open and curious. Warms up quickly.' },
  { key: 'standard', label: 'Standard', blurb: 'Busy and skeptical. Make it relevant.' },
  { key: 'hostile', label: 'Hostile', blurb: 'Resistant and short on time. Brush-offs incoming.' },
];

// Difficulty behavior text used when seeding roleplay_config/difficulty.
export const DEFAULT_DIFFICULTY_CONFIG = {
  cooperative: {
    label: 'Cooperative',
    behavior:
      'You are curious and fairly open. You give the rep room: you let them ' +
      'finish their opener, ask a clarifying question or two, and share a ' +
      'little about your situation when asked a good question. You still expect ' +
      'the rep to earn your time and be relevant, but you are not trying to get ' +
      'off the phone.',
  },
  standard: {
    label: 'Standard',
    behavior:
      'You are a busy, mildly skeptical professional. You open guarded and make ' +
      'the rep justify why you should keep listening. You engage if the rep is ' +
      'relevant and specific; you push back on vague value claims. You warm up ' +
      'only when the rep shows they understand your world.',
  },
  hostile: {
    label: 'Hostile',
    behavior:
      'You are short on time and openly resistant. You interrupt and throw ' +
      'brush-offs ("just email me", "we already have something", "not ' +
      'interested"). The rep has to handle real objections and stay composed to ' +
      'keep you on the line. You reward genuine relevance and punish scripts.',
  },
};

export const DEFAULT_RUBRIC = {
  scale: '1 (poor) to 5 (excellent)',
  categories: [
    { key: 'opener', label: 'Opener & permission', guidance: 'Earned the right to continue in the first 15–20s.' },
    { key: 'discovery', label: 'Discovery & listening', guidance: 'Asked relevant open questions and adapted.' },
    { key: 'value', label: 'Value articulation', guidance: 'Connected MeltPlan to this prospect concretely.' },
    { key: 'objections', label: 'Objection handling', guidance: 'Stayed composed; acknowledged and reframed.' },
    { key: 'closing', label: 'Next step / close', guidance: 'Drove to a specific, low-friction next step.' },
  ],
};

// A couple of generic starter personas so the tool works on day one. Refined
// in-app via the persona editor. `id` is a kebab-case slug (doc id).
export const STARTER_PERSONAS = [
  {
    id: 'busy-ops-director',
    title: 'Operations Director',
    company: 'A mid-size firm',
    track: 'cold-open',
    brief:
      'You run operations at a ~200-person company. Your day is back-to-back. ' +
      'You get pitched constantly and screen hard. You care about outcomes and ' +
      'your team’s time, not features. You have no idea who MeltPlan is.',
    knownObjections: ['We already have a process for that.', 'Can you just send me an email?'],
    difficultyOverrides: {},
    archived: false,
  },
  {
    id: 'skeptical-project-lead',
    title: 'Project Lead',
    company: 'An engineering consultancy',
    track: 'discovery',
    brief:
      'You lead delivery on client projects. You are analytical and want proof, ' +
      'not promises. You will engage with someone who clearly understands the ' +
      'messiness of coordinating projects, but you shut down generic pitches.',
    knownObjections: ['How is this different from what we use now?', 'I’d need to see real numbers.'],
    difficultyOverrides: {},
    archived: false,
  },
];

export function slugifyPersonaId(title) {
  return (title || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

export function trackLabel(key) {
  return TRACKS.find((t) => t.key === key)?.label || key;
}

export function difficultyLabel(key) {
  return DIFFICULTIES.find((d) => d.key === key)?.label || key;
}
